<!-- src/views/admin/MembersView.vue -->
<template>
  <div class="container-fluid py-3 py-md-4">
    <!-- Header -->
    <div
      class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3"
    >
      <div>
        <h3 class="fw-bold mb-1">Members</h3>
        <div class="text-muted small">
          Manage member profiles, roles, active status, and beneficiaries.
        </div>

        <div class="mt-2 d-flex flex-wrap gap-2 align-items-center">
          <span class="badge text-bg-light border">
            Results: <strong class="ms-1">{{ meta.total || 0 }}</strong>
          </span>

          <span v-if="loading" class="badge text-bg-light border">
            <i class="bi bi-hourglass-split me-1"></i>Loading…
          </span>

          <span v-if="activeFilterCount > 0" class="badge text-bg-primary">
            Filters: <strong class="ms-1">{{ activeFilterCount }}</strong>
          </span>
        </div>
      </div>

      <div class="d-flex gap-2 flex-wrap">
        <button
          class="btn btn-outline-success"
          :disabled="!isViewingActiveYear"
          @click="openImport"
        >
          <i class="bi bi-upload me-2"></i>Import Members
        </button>

        <button
          class="btn btn-primary"
          :disabled="!isViewingActiveYear"
          @click="openCreate"
        >
          <i class="bi bi-person-plus me-2"></i>Add Member
        </button>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <!-- Financial year selector -->
    <div class="card shadow-sm border-0 mb-3">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-12 col-md-5 col-lg-4">
            <label class="form-label small text-muted mb-1">
              Financial Year
            </label>
            <select
              v-model="selectedFinancialYearId"
              class="form-select"
              :disabled="loadingYears || loading"
              @change="onFinancialYearChanged"
            >
              <option
                v-for="year in financialYears"
                :key="year.id"
                :value="String(year.id)"
              >
                {{ financialYearLabel(year) }}
              </option>
            </select>
          </div>

          <div class="col-12 col-md">
            <div v-if="selectedFinancialYear" class="financial-year-summary">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <strong>{{ financialYearLabel(selectedFinancialYear) }}</strong>

                <span
                  class="badge rounded-pill"
                  :class="
                    isViewingActiveYear
                      ? 'text-bg-success'
                      : 'text-bg-secondary'
                  "
                >
                  {{ isViewingActiveYear ? "Active year" : "Inactive year" }}
                </span>
              </div>

              <div class="small text-muted mt-1">
                {{ formatDate(selectedFinancialYear.start_date) }}
                –
                {{ formatDate(selectedFinancialYear.end_date) }}
              </div>
            </div>
          </div>

          <div v-if="!isViewingActiveYear" class="col-12 col-lg-auto">
            <button
              type="button"
              class="btn btn-outline-primary"
              :disabled="loading"
              @click="goToActiveYear"
            >
              <i class="bi bi-calendar-check me-1"></i>
              Return to active year
            </button>
          </div>
        </div>

        <div
          v-if="!isViewingActiveYear"
          class="alert alert-warning border mt-3 mb-0 py-2"
        >
          <i class="bi bi-info-circle me-1"></i>
          You are viewing an inactive financial year. Opening balances may still
          be entered until the year is formally closed. Operational entries and
          commitments remain limited to the active year.
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card shadow-sm border-0 mb-3">
      <div class="card-body">
        <div class="row g-2">
          <div class="col-12 col-lg-5">
            <label class="form-label small text-muted mb-1">Search</label>
            <div class="input-group">
              <span class="input-group-text bg-white">
                <i class="bi bi-search"></i>
              </span>
              <input
                v-model.trim="filters.q"
                class="form-control"
                placeholder="Name, phone, email…"
                @keyup.enter="applyFilters"
                :disabled="loading"
              />
              <button
                class="btn btn-outline-secondary"
                @click="applyFilters"
                :disabled="loading"
              >
                Apply
              </button>
            </div>
          </div>

          <div class="col-12 col-sm-6 col-lg-3">
            <label class="form-label small text-muted mb-1">Role</label>
            <select
              v-model="filters.role"
              class="form-select"
              @change="applyFilters"
              :disabled="loading"
            >
              <option value="">All roles</option>
              <option value="chair">Chair</option>
              <option value="treasurer">Treasurer</option>
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div class="col-12 col-sm-6 col-lg-2">
            <label class="form-label small text-muted mb-1">Status</label>
            <select
              v-model="filters.is_active"
              class="form-select"
              @change="applyFilters"
              :disabled="loading"
            >
              <option value="">All</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
          </div>

          <div class="col-12 col-lg-2 d-grid">
            <label class="form-label small text-muted mb-1 d-none d-lg-block"
              >&nbsp;</label
            >
            <button
              class="btn btn-outline-dark btn-sm"
              @click="resetFilters"
              :disabled="loading"
            >
              <i class="bi bi-arrow-counterclockwise me-1"></i>Clear
            </button>
          </div>
        </div>

        <div v-if="activeFilterCount > 0" class="mt-3 d-flex flex-wrap gap-2">
          <span v-if="filters.q" class="chip">
            Search: <strong class="ms-1">{{ filters.q }}</strong>
            <button
              class="chip-x"
              type="button"
              @click="
                filters.q = '';
                applyFilters();
              "
            >
              ×
            </button>
          </span>

          <span v-if="filters.role" class="chip">
            Role: <strong class="ms-1">{{ filters.role }}</strong>
            <button
              class="chip-x"
              type="button"
              @click="
                filters.role = '';
                applyFilters();
              "
            >
              ×
            </button>
          </span>

          <span v-if="filters.is_active !== ''" class="chip">
            Status:
            <strong class="ms-1">{{
              filters.is_active === "1" ? "Active" : "Inactive"
            }}</strong>
            <button
              class="chip-x"
              type="button"
              @click="
                filters.is_active = '';
                applyFilters();
              "
            >
              ×
            </button>
          </span>

          <button
            class="btn btn-sm btn-outline-secondary ms-auto"
            @click="resetFilters"
            :disabled="loading"
          >
            Clear all
          </button>
        </div>
      </div>
    </div>

    <!-- Desktop table -->
    <div class="card shadow-sm border-0 d-none d-md-block">
      <div class="card-body p-0">
        <div class="table-responsive members-table-wrap">
          <table class="table table-hover align-middle mb-0">
            <thead class="table-light">
              <tr>
                <th class="member-col">Member</th>
                <th class="phone-col">Phone</th>
                <th class="email-col">Email</th>
                <th class="role-col">Role</th>
                <th class="status-col">Status</th>
                <th class="opening-col">Opening</th>
                <th class="commitment-col">Commitment</th>
                <th v-if="allowsBeneficiaries" class="beneficiary-col">
                  Beneficiaries
                </th>
                <th class="action-col text-end"></th>
              </tr>
            </thead>

            <tbody>
              <template v-for="m in rows" :key="m.id">
                <tr>
                  <td>
                    <div class="d-flex align-items-center gap-2">
                      <div class="avatar">{{ initials(m.name) }}</div>
                      <div class="min-w-0">
                        <div class="fw-semibold text-truncate">
                          {{ m.name }}
                        </div>
                        <div class="text-muted small">ID: {{ m.id }}</div>
                      </div>
                    </div>
                  </td>

                  <td class="fw-semibold">{{ m.phone || "-" }}</td>

                  <td class="text-muted email-cell">
                    <span class="email-text">{{ m.email || "-" }}</span>
                  </td>

                  <td>
                    <span class="badge text-bg-light border text-uppercase">
                      {{ m.role || "member" }}
                    </span>
                  </td>

                  <td>
                    <span
                      class="badge rounded-pill"
                      :class="
                        m.is_active ? 'text-bg-success' : 'text-bg-secondary'
                      "
                    >
                      {{ m.is_active ? "Active" : "Inactive" }}
                    </span>
                  </td>

                  <td>
                    <button
                      v-if="!!m.opening_balance_any"
                      type="button"
                      class="badge text-bg-success border-0"
                      style="cursor: pointer"
                      @click="viewOpeningBalance(m)"
                    >
                      <i class="bi bi-eye me-1"></i>View
                    </button>
                    <button
                      v-else-if="canManageOpeningBalances"
                      type="button"
                      class="badge text-bg-warning border-0"
                      style="cursor: pointer"
                      @click="openOpeningBalance(m)"
                    >
                      <i class="bi bi-plus-circle me-1"></i>Set
                    </button>
                    <span v-else class="badge text-bg-light border text-muted">
                      None
                    </span>
                  </td>

                  <td>
                    <button
                      v-if="!!m.commitment_active"
                      type="button"
                      class="badge text-bg-success border-0"
                      style="cursor: pointer"
                      @click="viewCommitment(m)"
                    >
                      <i class="bi bi-eye me-1"></i>View
                    </button>
                    <button
                      v-else-if="isViewingActiveYear"
                      type="button"
                      class="badge text-bg-warning border-0"
                      style="cursor: pointer"
                      @click="openCommitment(m)"
                    >
                      <i class="bi bi-plus-circle me-1"></i>Set
                    </button>
                    <span v-else class="badge text-bg-light border text-muted">
                      None
                    </span>
                  </td>

                  <td v-if="allowsBeneficiaries">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-info"
                      @click="toggleBeneficiaries(m)"
                    >
                      <i class="bi bi-people me-1"></i>
                      {{ beneficiaryCount(m) }}
                    </button>
                  </td>

                  <td class="text-end position-relative">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary action-btn"
                      title="Actions"
                      @click.stop="toggleMenu(m.id)"
                    >
                      <i class="bi bi-three-dots-vertical"></i>
                    </button>

                    <div
                      v-if="openMenuId === m.id"
                      class="action-pop shadow-sm"
                      @click.stop
                    >
                      <button
                        class="action-item"
                        type="button"
                        @click="
                          openStatement(m);
                          closeMenu();
                        "
                      >
                        <i class="bi bi-file-earmark-text me-2"></i>Statement
                      </button>

                      <button
                        v-if="allowsBeneficiaries"
                        class="action-item"
                        type="button"
                        @click="
                          toggleBeneficiaries(m);
                          closeMenu();
                        "
                      >
                        <i class="bi bi-people me-2"></i>Manage Beneficiaries
                      </button>

                      <button
                        class="action-item"
                        type="button"
                        @click="
                          openEdit(m);
                          closeMenu();
                        "
                      >
                        <i class="bi bi-pencil-square me-2"></i>Edit
                      </button>

                      <button
                        v-if="
                          canManageOpeningBalances && !m.opening_balance_any
                        "
                        class="action-item"
                        type="button"
                        @click="
                          openOpeningBalance(m);
                          closeMenu();
                        "
                      >
                        <i class="bi bi-wallet2 me-2"></i>Set Opening Balance
                      </button>

                      <button
                        v-if="isViewingActiveYear && !m.commitment_active"
                        class="action-item"
                        type="button"
                        @click="
                          openCommitment(m);
                          closeMenu();
                        "
                      >
                        <i class="bi bi-calendar-check me-2"></i>Set Commitment
                      </button>

                      <div class="action-divider"></div>

                      <button
                        class="action-item"
                        type="button"
                        :class="m.is_active ? 'text-danger' : 'text-success'"
                        @click="
                          toggleActive(m);
                          closeMenu();
                        "
                      >
                        <i
                          class="bi me-2"
                          :class="
                            m.is_active ? 'bi-person-x' : 'bi-person-check'
                          "
                        ></i>
                        {{ m.is_active ? "Deactivate" : "Activate" }}
                      </button>
                    </div>
                  </td>
                </tr>

                <tr
                  v-if="
                    allowsBeneficiaries && expandedBeneficiaryMemberId === m.id
                  "
                >
                  <td :colspan="desktopColumnCount" class="bg-light-subtle p-0">
                    <div class="beneficiary-panel p-3">
                      <div
                        class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
                      >
                        <div>
                          <div class="fw-semibold">
                            Beneficiaries of {{ m.name }}
                          </div>
                          <div class="small text-muted">
                            Create, update, activate, deactivate, or remove
                            beneficiary records.
                          </div>
                        </div>

                        <button
                          type="button"
                          class="btn btn-sm btn-primary"
                          :disabled="!isViewingActiveYear"
                          @click="openCreateBeneficiary(m)"
                        >
                          <i class="bi bi-person-plus me-1"></i>
                          Add Beneficiary
                        </button>
                      </div>

                      <div
                        v-if="beneficiaryCount(m) === 0"
                        class="alert alert-light border mb-0"
                      >
                        No beneficiaries registered for this member.
                      </div>

                      <div v-else class="table-responsive">
                        <table class="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>Beneficiary</th>
                              <th>Relationship</th>
                              <th>Date of birth</th>
                              <th>Joined</th>
                              <th>Registration fee</th>
                              <th>Status</th>
                              <th>Opening</th>
                              <th>Commitment</th>
                              <th class="text-end">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr
                              v-for="beneficiary in normalizedBeneficiaries(m)"
                              :key="beneficiary.id"
                            >
                              <td>
                                <div class="fw-semibold">
                                  {{ beneficiary.name }}
                                </div>
                                <div class="small text-muted">
                                  ID: {{ beneficiary.id }}
                                </div>
                              </td>
                              <td>{{ beneficiary.relationship || "-" }}</td>
                              <td>
                                {{ formatDate(beneficiary.date_of_birth) }}
                              </td>
                              <td>{{ formatDate(beneficiary.joined_at) }}</td>
                              <td>
                                <div>
                                  {{
                                    formatMoney(
                                      beneficiary.registration_fee_amount,
                                    )
                                  }}
                                </div>
                                <span
                                  class="badge"
                                  :class="
                                    registrationStatusClass(
                                      beneficiary.registration_fee_status,
                                    )
                                  "
                                >
                                  {{
                                    formatRegistrationStatus(
                                      beneficiary.registration_fee_status,
                                    )
                                  }}
                                </span>
                              </td>
                              <td>
                                <span
                                  class="badge rounded-pill"
                                  :class="
                                    beneficiary.is_active
                                      ? 'text-bg-success'
                                      : 'text-bg-secondary'
                                  "
                                >
                                  {{
                                    beneficiary.is_active
                                      ? "Active"
                                      : "Inactive"
                                  }}
                                </span>
                              </td>
                              <td>
                                <button
                                  v-if="beneficiary.opening_balance_any"
                                  type="button"
                                  class="badge text-bg-success border-0"
                                  style="cursor: pointer"
                                  @click="
                                    viewBeneficiaryOpeningBalance(
                                      beneficiary,
                                      m,
                                    )
                                  "
                                >
                                  <i class="bi bi-eye me-1"></i>
                                  View
                                </button>

                                <button
                                  v-else-if="canManageOpeningBalances"
                                  type="button"
                                  class="badge text-bg-warning border-0"
                                  style="cursor: pointer"
                                  @click="
                                    openBeneficiaryOpeningBalance(
                                      beneficiary,
                                      m,
                                    )
                                  "
                                >
                                  <i class="bi bi-plus-circle me-1"></i>
                                  Set
                                </button>

                                <span
                                  v-else
                                  class="badge text-bg-light border text-muted"
                                >
                                  None
                                </span>
                              </td>
                              <td>
                                <button
                                  v-if="beneficiary.commitment_active"
                                  type="button"
                                  class="badge text-bg-success border-0"
                                  style="cursor: pointer"
                                  @click="
                                    viewBeneficiaryCommitment(beneficiary, m)
                                  "
                                >
                                  <i class="bi bi-eye me-1"></i>
                                  View
                                </button>

                                <button
                                  v-else-if="isViewingActiveYear"
                                  type="button"
                                  class="badge text-bg-warning border-0"
                                  style="cursor: pointer"
                                  @click="
                                    openBeneficiaryCommitment(beneficiary, m)
                                  "
                                >
                                  <i class="bi bi-plus-circle me-1"></i>
                                  Set
                                </button>

                                <span
                                  v-else
                                  class="badge text-bg-light border text-muted"
                                >
                                  None
                                </span>
                              </td>
                              <td class="text-end">
                                <div
                                  class="d-inline-flex flex-wrap gap-1 justify-content-end"
                                >
                                  <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    :disabled="!isViewingActiveYear"
                                    @click="openEditBeneficiary(beneficiary, m)"
                                  >
                                    <i class="bi bi-pencil-square"></i>
                                  </button>

                                  <button
                                    type="button"
                                    class="btn btn-sm"
                                    :class="
                                      beneficiary.is_active
                                        ? 'btn-outline-danger'
                                        : 'btn-outline-success'
                                    "
                                    :disabled="!isViewingActiveYear"
                                    @click="
                                      toggleBeneficiaryActive(beneficiary, m)
                                    "
                                  >
                                    <i
                                      class="bi"
                                      :class="
                                        beneficiary.is_active
                                          ? 'bi-person-x'
                                          : 'bi-person-check'
                                      "
                                    ></i>
                                  </button>

                                  <button
                                    v-if="!beneficiary.is_active"
                                    type="button"
                                    class="btn btn-sm btn-outline-danger"
                                    :disabled="!isViewingActiveYear"
                                    @click="removeBeneficiary(beneficiary, m)"
                                  >
                                    <i class="bi bi-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>

              <tr v-if="!loading && rows.length === 0">
                <td
                  :colspan="desktopColumnCount"
                  class="text-center text-muted py-4"
                >
                  No members found.
                </td>
              </tr>

              <tr v-if="loading">
                <td
                  :colspan="desktopColumnCount"
                  class="text-center text-muted py-4"
                >
                  <i class="bi bi-hourglass-split me-2"></i>Loading members…
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-2 p-3 border-top"
        >
          <small class="text-muted">
            Showing <strong>{{ meta.from || 0 }}</strong
            >–<strong>{{ meta.to || 0 }}</strong> of
            <strong>{{ meta.total || 0 }}</strong>
            <span v-if="meta.current_page && meta.last_page" class="ms-2">
              • Page <strong>{{ meta.current_page }}</strong> /
              <strong>{{ meta.last_page }}</strong>
            </span>
          </small>

          <div class="btn-group">
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="loading || meta.current_page <= 1"
              @click="go(meta.current_page - 1)"
            >
              <i class="bi bi-chevron-left"></i> Prev
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              :disabled="loading || meta.current_page >= meta.last_page"
              @click="go(meta.current_page + 1)"
            >
              Next <i class="bi bi-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Mobile cards -->
    <div class="d-md-none">
      <div v-if="loading" class="card shadow-sm border-0 mb-2">
        <div class="card-body text-muted">
          <i class="bi bi-hourglass-split me-2"></i>Loading members…
        </div>
      </div>

      <div v-else-if="rows.length === 0" class="card shadow-sm border-0">
        <div class="card-body text-center text-muted py-4">
          No members found.
        </div>
      </div>

      <div v-else class="d-grid gap-2">
        <div v-for="m in rows" :key="m.id" class="card shadow-sm border-0">
          <div class="card-body">
            <div class="d-flex justify-content-between gap-2">
              <div class="d-flex align-items-center gap-2 min-w-0">
                <div class="avatar">{{ initials(m.name) }}</div>
                <div class="min-w-0">
                  <div class="fw-semibold text-truncate">{{ m.name }}</div>
                  <div class="text-muted small">ID: {{ m.id }}</div>
                </div>
              </div>

              <span
                class="badge rounded-pill align-self-start"
                :class="m.is_active ? 'text-bg-success' : 'text-bg-secondary'"
              >
                {{ m.is_active ? "Active" : "Inactive" }}
              </span>
            </div>

            <div class="mt-2 d-grid gap-1 small">
              <div class="d-flex justify-content-between">
                <span class="text-muted">Phone</span>
                <span class="fw-semibold">{{ m.phone || "-" }}</span>
              </div>

              <div class="d-flex justify-content-between">
                <span class="text-muted">Role</span>
                <span class="badge text-bg-light border text-uppercase">
                  {{ m.role || "member" }}
                </span>
              </div>

              <div class="d-flex justify-content-between">
                <span class="text-muted">Email</span>
                <span class="text-truncate" style="max-width: 55vw">
                  {{ m.email || "-" }}
                </span>
              </div>

              <div class="d-grid gap-2 mt-2 mobile-actions">
                <div class="d-flex gap-2 flex-wrap">
                  <button
                    class="btn btn-sm"
                    :class="
                      m.opening_balance_any
                        ? 'btn-outline-success'
                        : canManageOpeningBalances
                        ? 'btn-outline-warning'
                        : 'btn-outline-secondary'
                    "
                    :disabled="
                      !m.opening_balance_any && !canManageOpeningBalances
                    "
                    @click="
                      m.opening_balance_any
                        ? viewOpeningBalance(m)
                        : canManageOpeningBalances
                        ? openOpeningBalance(m)
                        : null
                    "
                  >
                    <i
                      class="bi"
                      :class="
                        m.opening_balance_any ? 'bi-eye' : 'bi-plus-circle'
                      "
                    ></i>
                    Opening
                  </button>

                  <button
                    class="btn btn-sm"
                    :class="
                      m.commitment_active
                        ? 'btn-outline-success'
                        : isViewingActiveYear
                        ? 'btn-outline-warning'
                        : 'btn-outline-secondary'
                    "
                    :disabled="!m.commitment_active && !isViewingActiveYear"
                    @click="
                      m.commitment_active
                        ? viewCommitment(m)
                        : isViewingActiveYear
                        ? openCommitment(m)
                        : null
                    "
                  >
                    <i
                      class="bi"
                      :class="m.commitment_active ? 'bi-eye' : 'bi-plus-circle'"
                    ></i>
                    Commitment
                  </button>
                </div>

                <div class="d-flex gap-2 flex-wrap">
                  <button
                    class="btn btn-sm btn-outline-primary"
                    type="button"
                    @click="openStatement(m)"
                  >
                    <i class="bi bi-file-earmark-text"></i>
                    Statement
                  </button>

                  <button
                    class="btn btn-sm btn-outline-secondary"
                    @click="openEdit(m)"
                  >
                    <i class="bi bi-pencil-square"></i>
                    Edit
                  </button>
                </div>
              </div>

              <div v-if="allowsBeneficiaries" class="mt-3">
                <button
                  type="button"
                  class="btn btn-sm btn-outline-info w-100"
                  @click="toggleBeneficiaries(m)"
                >
                  <i class="bi bi-people me-1"></i>
                  Beneficiaries ({{ beneficiaryCount(m) }})
                </button>

                <div
                  v-if="expandedBeneficiaryMemberId === m.id"
                  class="beneficiary-mobile-panel mt-2"
                >
                  <div class="d-flex justify-content-end mb-2">
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      :disabled="!isViewingActiveYear"
                      @click="openCreateBeneficiary(m)"
                    >
                      <i class="bi bi-person-plus me-1"></i>Add
                    </button>
                  </div>

                  <div
                    v-if="beneficiaryCount(m) === 0"
                    class="small text-muted"
                  >
                    No beneficiaries registered.
                  </div>

                  <div
                    v-for="beneficiary in normalizedBeneficiaries(m)"
                    :key="beneficiary.id"
                    class="beneficiary-mobile-item"
                  >
                    <div class="d-flex justify-content-between gap-2">
                      <div>
                        <div class="fw-semibold">
                          {{ beneficiary.name }}
                        </div>
                        <div class="small text-muted">
                          {{
                            beneficiary.relationship || "Relationship not set"
                          }}
                        </div>
                      </div>

                      <span
                        class="badge align-self-start"
                        :class="
                          beneficiary.is_active
                            ? 'text-bg-success'
                            : 'text-bg-secondary'
                        "
                      >
                        {{ beneficiary.is_active ? "Active" : "Inactive" }}
                      </span>
                    </div>

                    <div class="small text-muted mt-1">
                      Joined: {{ formatDate(beneficiary.joined_at) }}
                    </div>

                    <div class="d-flex flex-wrap gap-1 mt-2">
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-secondary"
                        :disabled="!isViewingActiveYear"
                        @click="openEditBeneficiary(beneficiary, m)"
                      >
                        <i class="bi bi-pencil-square me-1"></i>Edit
                      </button>

                      <button
                        type="button"
                        class="btn btn-sm"
                        :class="
                          beneficiary.is_active
                            ? 'btn-outline-danger'
                            : 'btn-outline-success'
                        "
                        :disabled="!isViewingActiveYear"
                        @click="toggleBeneficiaryActive(beneficiary, m)"
                      >
                        {{ beneficiary.is_active ? "Deactivate" : "Activate" }}
                      </button>

                      <button
                        v-if="!beneficiary.is_active"
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        :disabled="!isViewingActiveYear"
                        @click="removeBeneficiary(beneficiary, m)"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <button
                class="btn btn-sm mt-2"
                :class="
                  m.is_active ? 'btn-outline-danger' : 'btn-outline-success'
                "
                @click="toggleActive(m)"
              >
                <i
                  class="bi me-1"
                  :class="m.is_active ? 'bi-person-x' : 'bi-person-check'"
                ></i>
                {{ m.is_active ? "Deactivate" : "Activate" }}
              </button>
            </div>
          </div>
        </div>

        <div class="card shadow-sm border-0">
          <div
            class="card-body d-flex justify-content-between align-items-center"
          >
            <small class="text-muted">
              Page <strong>{{ meta.current_page }}</strong> /
              <strong>{{ meta.last_page }}</strong>
            </small>

            <div class="btn-group">
              <button
                class="btn btn-outline-secondary btn-sm"
                :disabled="loading || meta.current_page <= 1"
                @click="go(meta.current_page - 1)"
              >
                <i class="bi bi-chevron-left"></i>
              </button>
              <button
                class="btn btn-outline-secondary btn-sm"
                :disabled="loading || meta.current_page >= meta.last_page"
                @click="go(meta.current_page + 1)"
              >
                <i class="bi bi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <MemberModal
      v-if="showCreate"
      title="Add Member"
      :initial="null"
      @close="showCreate = false"
      @saved="onSaved"
    />

    <MemberModal
      v-if="showEdit"
      title="Edit Member"
      :initial="editing"
      @close="closeEdit"
      @saved="onSaved"
    />

    <ImportMembersModal
      v-if="showImport"
      @close="showImport = false"
      @imported="onImportCompleted"
    />

    <OpeningBalanceModal
      ref="openingBalanceModal"
      :members="rows"
      :financial-year="selectedFinancialYear"
      @saved="onOpeningSaved"
    />

    <CommitmentModal
      ref="commitmentModal"
      :members="rows"
      :financial-year="selectedFinancialYear"
      @saved="onCommitmentSaved"
    />

    <ViewOpeningBalanceModal ref="viewOpeningBalanceModal" />
    <ViewCommitmentModal ref="viewCommitmentModal" />

    <BeneficiaryModal
      v-if="showBeneficiaryModal && selectedBeneficiaryGuardian"
      :guardian="selectedBeneficiaryGuardian"
      :initial="editingBeneficiary"
      @close="closeBeneficiaryModal"
      @saved="onBeneficiarySaved"
    />
    <BeneficiaryOpeningBalanceModal
      ref="beneficiaryOpeningBalanceModal"
      :members="rows"
      :financial-year="selectedFinancialYear"
      @saved="onOpeningSaved"
    />

    <BeneficiaryCommitmentModal
      ref="beneficiaryCommitmentModal"
      :members="rows"
      :financial-year="selectedFinancialYear"
      @saved="onCommitmentSaved"
    />

    <ViewBeneficiaryOpeningBalanceModal
      ref="viewBeneficiaryOpeningBalanceModal"
    />

    <ViewBeneficiaryCommitmentModal ref="viewBeneficiaryCommitmentModal" />
  </div>
</template>

<script>
import { listMembers, toggleMemberActive } from "@/services/members";
import http from "@/services/http";

import MemberModal from "@/components/MemberModal.vue";
import OpeningBalanceModal from "@/components/OpeningBalanceModal.vue";
import ViewOpeningBalanceModal from "@/components/ViewOpeningBalanceModal.vue";
import CommitmentModal from "@/components/CommitmentModal.vue";
import ViewCommitmentModal from "@/components/ViewCommitmentModal.vue";
import ImportMembersModal from "@/components/ImportMembersModal.vue";
import BeneficiaryModal from "@/components/BeneficiaryModal.vue";

import BeneficiaryOpeningBalanceModal from "@/components/beneficiaries/BeneficiaryOpeningBalanceModal.vue";
import BeneficiaryCommitmentModal from "@/components/beneficiaries/BeneficiaryCommitmentModal.vue";

import ViewBeneficiaryOpeningBalanceModal from "@/components/beneficiaries/ViewBeneficiaryOpeningBalanceModal.vue";
import ViewBeneficiaryCommitmentModal from "@/components/beneficiaries/ViewBeneficiaryCommitmentModal.vue";
import {
  toggleBeneficiaryActive as updateBeneficiaryActive,
  deleteBeneficiary,
} from "@/services/beneficiaries";

import { groupAllowsBeneficiaries } from "@/utils/activeGroup";

export default {
  name: "MembersView",
  components: {
    MemberModal,
    OpeningBalanceModal,
    ViewOpeningBalanceModal,
    CommitmentModal,
    ViewCommitmentModal,
    ImportMembersModal,
    BeneficiaryModal,

    BeneficiaryOpeningBalanceModal,
    BeneficiaryCommitmentModal,
    ViewBeneficiaryOpeningBalanceModal,
    ViewBeneficiaryCommitmentModal,
  },

  data() {
    return {
      loading: false,
      loadingYears: false,
      error: "",

      financialYears: [],
      selectedFinancialYearId: "",

      filters: { q: "", role: "", is_active: "" },
      page: 1,

      rows: [],
      meta: { current_page: 1, last_page: 1, from: 0, to: 0, total: 0 },

      showCreate: false,
      showEdit: false,
      showImport: false,
      editing: null,

      _qTimer: null,

      openMenuId: null,

      expandedBeneficiaryMemberId: null,
      showBeneficiaryModal: false,
      selectedBeneficiaryGuardian: null,
      editingBeneficiary: null,
    };
  },

  computed: {
    allowsBeneficiaries() {
      return groupAllowsBeneficiaries();
    },

    desktopColumnCount() {
      return this.allowsBeneficiaries ? 9 : 8;
    },

    selectedFinancialYear() {
      return (
        this.financialYears.find(
          (year) => String(year.id) === String(this.selectedFinancialYearId),
        ) || null
      );
    },

    activeFinancialYear() {
      return (
        this.financialYears.find(
          (year) => Boolean(year.is_active) || year.status === "active",
        ) || null
      );
    },

    isViewingActiveYear() {
      if (!this.selectedFinancialYear) return false;

      return (
        Boolean(this.selectedFinancialYear.is_active) ||
        this.selectedFinancialYear.status === "active"
      );
    },

    canManageOpeningBalances() {
      return Boolean(this.selectedFinancialYear?.id);
    },

    selectedYearStartPeriod() {
      return this.periodFromDate(this.selectedFinancialYear?.start_date);
    },

    selectedYearEndPeriod() {
      return this.periodFromDate(this.selectedFinancialYear?.end_date);
    },

    selectedAsOfPeriod() {
      const current = this.monthKeyToday();

      if (!this.selectedFinancialYear) return current;

      if (current < this.selectedYearStartPeriod) {
        return this.selectedYearStartPeriod;
      }

      if (current > this.selectedYearEndPeriod) {
        return this.selectedYearEndPeriod;
      }

      return current;
    },

    activeFilterCount() {
      let n = 0;
      if (this.filters.q) n++;
      if (this.filters.role) n++;
      if (this.filters.is_active !== "") n++;
      return n;
    },
  },

  watch: {
    "filters.q"() {
      clearTimeout(this._qTimer);
      this._qTimer = setTimeout(() => {
        this.page = 1;
        this.load();
      }, 350);
    },
  },

  methods: {
    beneficiaryCount(member) {
      return Array.isArray(member?.beneficiaries)
        ? member.beneficiaries.length
        : 0;
    },

    normalizedBeneficiaries(member) {
      return Array.isArray(member?.beneficiaries) ? member.beneficiaries : [];
    },

    toggleBeneficiaries(member) {
      if (!this.allowsBeneficiaries) return;

      this.expandedBeneficiaryMemberId =
        this.expandedBeneficiaryMemberId === member.id ? null : member.id;
    },

    openCreateBeneficiary(member) {
      if (!this.allowsBeneficiaries || !this.isViewingActiveYear) {
        return;
      }

      this.selectedBeneficiaryGuardian = member;
      this.editingBeneficiary = null;
      this.showBeneficiaryModal = true;
    },

    openEditBeneficiary(beneficiary, guardian) {
      if (!this.allowsBeneficiaries || !this.isViewingActiveYear) {
        return;
      }

      this.selectedBeneficiaryGuardian = guardian;
      this.editingBeneficiary = { ...beneficiary };
      this.showBeneficiaryModal = true;
    },

    closeBeneficiaryModal() {
      this.showBeneficiaryModal = false;
      this.selectedBeneficiaryGuardian = null;
      this.editingBeneficiary = null;
    },

    async onBeneficiarySaved() {
      const guardianId = this.selectedBeneficiaryGuardian?.id;

      this.closeBeneficiaryModal();
      await this.load();

      if (guardianId) {
        this.expandedBeneficiaryMemberId = guardianId;
      }
    },

    async toggleBeneficiaryActive(beneficiary, guardian) {
      if (!this.isViewingActiveYear) return;

      const action = beneficiary.is_active ? "deactivate" : "activate";

      if (!confirm(`Are you sure you want to ${action} ${beneficiary.name}?`)) {
        return;
      }

      try {
        await updateBeneficiaryActive(beneficiary.id, !beneficiary.is_active);

        await this.load();
        this.expandedBeneficiaryMemberId = guardian.id;
      } catch (error) {
        alert(
          error?.response?.data?.message ||
            "Failed to update beneficiary status.",
        );
      }
    },

    async removeBeneficiary(beneficiary, guardian) {
      if (!this.isViewingActiveYear) return;

      if (
        !confirm(
          `Permanently delete ${beneficiary.name}? This is only allowed when the beneficiary has no financial records.`,
        )
      ) {
        return;
      }

      try {
        await deleteBeneficiary(beneficiary.id);

        await this.load();
        this.expandedBeneficiaryMemberId = guardian.id;
      } catch (error) {
        alert(
          error?.response?.data?.message || "Failed to delete beneficiary.",
        );
      }
    },

    openBeneficiaryOpeningBalance(beneficiary, guardian) {
      if (!this.selectedFinancialYear?.id) {
        this.error = "Select a financial year first.";
        return;
      }

      const period = this.selectedYearStartPeriod || this.monthKeyToday();

      const participant = {
        ...beneficiary,
        participant_type: "beneficiary",
        guardian_user_id: guardian.id,
        guardian_name: guardian.name,
        user_id: guardian.id,
      };

      const modal = this.$refs.beneficiaryOpeningBalanceModal;

      if (modal?.openForBeneficiary) {
        modal.openForBeneficiary(participant, {
          period,
          financialYear: this.selectedFinancialYear,
        });
        return;
      }

      if (modal?.open) {
        modal.open(participant, period, this.selectedFinancialYear);
      }
    },
    viewBeneficiaryOpeningBalance(beneficiary, guardian) {
      const participant = {
        ...beneficiary,
        participant_type: "beneficiary",
        guardian_user_id: guardian.id,
        guardian_name: guardian.name,
        user_id: guardian.id,
      };

      this.$refs.viewBeneficiaryOpeningBalanceModal?.open(
        participant,
        this.selectedAsOfPeriod,
        Number(this.selectedFinancialYearId),
      );
    },
    openBeneficiaryCommitment(beneficiary, guardian) {
      if (!this.isViewingActiveYear) {
        return;
      }

      const participant = {
        ...beneficiary,
        participant_type: "beneficiary",
        guardian_user_id: guardian.id,
        guardian_name: guardian.name,
        user_id: guardian.id,
      };

      const modal = this.$refs.beneficiaryCommitmentModal;

      if (modal?.openForBeneficiary) {
        modal.openForBeneficiary(participant, {
          period: this.selectedAsOfPeriod,
          financialYear: this.selectedFinancialYear,
        });
        return;
      }

      if (modal?.open) {
        modal.open(
          participant,
          this.selectedAsOfPeriod,
          this.selectedFinancialYear,
        );
      }
    },
    viewBeneficiaryCommitment(beneficiary, guardian) {
      const participant = {
        ...beneficiary,
        participant_type: "beneficiary",
        guardian_user_id: guardian.id,
        guardian_name: guardian.name,
        user_id: guardian.id,
      };

      this.$refs.viewBeneficiaryCommitmentModal?.open(
        participant,
        this.selectedAsOfPeriod,
        Number(this.selectedFinancialYearId),
      );
    },
    formatMoney(value) {
      const amount = Number(value || 0);

      return new Intl.NumberFormat("en-RW", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(Number.isFinite(amount) ? amount : 0);
    },

    formatRegistrationStatus(status) {
      if (!status) return "-";

      return String(status)
        .replaceAll("_", " ")
        .replace(/\b\w/g, (character) => character.toUpperCase());
    },

    registrationStatusClass(status) {
      return (
        {
          paid: "text-bg-success",
          pending: "text-bg-warning",
          waived: "text-bg-info",
          not_applicable: "text-bg-secondary",
        }[status] || "text-bg-light border text-muted"
      );
    },

    normalizeFinancialYears(payload) {
      const raw = payload?.data ?? payload ?? [];

      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw?.data)) return raw.data;

      return [];
    },

    financialYearLabel(year) {
      if (!year) return "Financial year";

      return (
        year.name ||
        year.year_key ||
        year.label ||
        `${this.formatDate(year.start_date)} – ${this.formatDate(
          year.end_date,
        )}`
      );
    },

    formatDate(value) {
      if (!value) return "-";

      const date = new Date(`${String(value).slice(0, 10)}T00:00:00`);

      if (Number.isNaN(date.getTime())) return String(value);

      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    },

    periodFromDate(value) {
      if (!value) return "";
      return String(value).slice(0, 7);
    },

    async loadFinancialYears() {
      this.loadingYears = true;

      try {
        const res = await http.get("/financial-year-rules");

        this.financialYears = this.normalizeFinancialYears(res.data).sort(
          (a, b) =>
            String(b.start_date || b.year_key || "").localeCompare(
              String(a.start_date || a.year_key || ""),
            ),
        );

        const active = this.financialYears.find(
          (year) => Boolean(year.is_active) || year.status === "active",
        );

        const savedFinancialYearId = localStorage.getItem(
          "members_financial_year_rule_id",
        );

        const savedExists =
          savedFinancialYearId &&
          this.financialYears.some(
            (year) => String(year.id) === String(savedFinancialYearId),
          );

        if (savedExists) {
          this.selectedFinancialYearId = String(savedFinancialYearId);
        } else if (
          !this.selectedFinancialYearId ||
          !this.financialYears.some(
            (year) => String(year.id) === String(this.selectedFinancialYearId),
          )
        ) {
          this.selectedFinancialYearId = active
            ? String(active.id)
            : this.financialYears[0]
            ? String(this.financialYears[0].id)
            : "";
        }
      } catch (e) {
        this.error =
          e?.response?.data?.message || "Failed to load financial years";
        this.financialYears = [];
        this.selectedFinancialYearId = "";
      } finally {
        this.loadingYears = false;
      }
    },

    async onFinancialYearChanged() {
      if (this.selectedFinancialYearId) {
        localStorage.setItem(
          "members_financial_year_rule_id",
          String(this.selectedFinancialYearId),
        );
      }

      this.page = 1;
      await this.load();
    },

    async goToActiveYear() {
      if (!this.activeFinancialYear) return;

      this.selectedFinancialYearId = String(this.activeFinancialYear.id);
      this.page = 1;
      await this.load();
    },

    toggleMenu(id) {
      this.openMenuId = this.openMenuId === id ? null : id;
    },

    closeMenu() {
      this.openMenuId = null;
    },

    onDocClick(e) {
      const inMenu = e.target.closest(".action-pop");
      const inBtn = e.target.closest(".action-btn");
      if (!inMenu && !inBtn) this.closeMenu();
    },

    initials(name) {
      const s = String(name || "").trim();
      if (!s) return "?";
      const parts = s.split(/\s+/).slice(0, 2);
      return parts.map((p) => p[0]?.toUpperCase()).join("");
    },

    monthKeyToday() {
      const t = new Date();
      const yyyy = t.getFullYear();
      const mm = String(t.getMonth() + 1).padStart(2, "0");
      return `${yyyy}-${mm}`;
    },

    openOpeningBalance(member) {
      if (!this.selectedFinancialYear?.id) {
        this.error = "Select a financial year first.";
        return;
      }

      const period = this.selectedYearStartPeriod || this.monthKeyToday();

      this.$refs.openingBalanceModal?.openForMember(member, {
        period,
        financialYear: this.selectedFinancialYear,
      });
    },

    viewOpeningBalance(member) {
      this.$refs.viewOpeningBalanceModal?.open(
        member,
        this.selectedAsOfPeriod,
        Number(this.selectedFinancialYearId),
      );
    },

    async onOpeningSaved() {
      await this.load();
    },

    openCommitment(member) {
      if (!this.isViewingActiveYear) return;

      const startPeriod = this.selectedAsOfPeriod;

      this.$refs.commitmentModal?.openForMember(member, {
        period: startPeriod,
        financialYear: this.selectedFinancialYear,
      });
    },

    viewCommitment(member) {
      this.$refs.viewCommitmentModal?.open(
        member,
        this.selectedAsOfPeriod,
        Number(this.selectedFinancialYearId),
      );
    },

    async onCommitmentSaved() {
      await this.load();
    },

    openStatement(member) {
      this.$router.push({
        path: `/admin/members/${member.id}/financial-statement`,
        query: {
          financial_year_rule_id: this.selectedFinancialYearId,
        },
      });
    },

    async load() {
      this.loading = true;
      this.error = "";
      this.closeMenu();

      try {
        if (!this.selectedFinancialYearId) {
          this.rows = [];
          this.meta = {
            current_page: 1,
            last_page: 1,
            from: 0,
            to: 0,
            total: 0,
          };
          return;
        }

        const period = this.selectedAsOfPeriod;

        const res = await listMembers({
          ...this.filters,
          page: this.page,
          financial_year_rule_id: this.selectedFinancialYearId,
          as_of_period: period,
        });

        /*
         * Revised backend response:
         * {
         *   financial_year: {...},
         *   data: { data: [...], current_page: ... }
         * }
         */
        const paginator = res.data?.data ?? res.data ?? {};

        const memberRows = Array.isArray(paginator?.data)
          ? paginator.data
          : Array.isArray(paginator)
          ? paginator
          : [];

        this.rows = memberRows.map((member) => ({
          ...member,
          beneficiaries:
            this.allowsBeneficiaries && Array.isArray(member.beneficiaries)
              ? member.beneficiaries
              : [],
        }));

        this.meta = {
          current_page: Number(paginator?.current_page || 1),
          last_page: Number(paginator?.last_page || 1),
          from: Number(paginator?.from || 0),
          to: Number(paginator?.to || 0),
          total: Number(paginator?.total || 0),
        };
      } catch (e) {
        this.error = e?.response?.data?.message || "Failed to load members";
        this.rows = [];
        this.meta = { current_page: 1, last_page: 1, from: 0, to: 0, total: 0 };
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      this.page = 1;
      this.load();
    },

    resetFilters() {
      this.filters = { q: "", role: "", is_active: "" };
      this.page = 1;
      this.load();
    },

    go(p) {
      if (!p || p < 1 || p === this.page) return;
      this.page = p;
      this.load();
    },

    openCreate() {
      if (!this.isViewingActiveYear) return;
      this.showCreate = true;
    },

    openImport() {
      if (!this.isViewingActiveYear) return;
      this.showImport = true;
    },

    openEdit(m) {
      this.editing = { ...m };
      this.showEdit = true;
    },

    closeEdit() {
      this.showEdit = false;
      this.editing = null;
    },

    async toggleActive(m) {
      const action = m.is_active ? "deactivate" : "activate";
      if (!confirm(`Are you sure you want to ${action} ${m.name}?`)) return;

      try {
        await toggleMemberActive(m.id, !m.is_active);
        await this.load();
      } catch (e) {
        alert(e?.response?.data?.message || "Failed to update status");
      }
    },

    async onSaved() {
      this.showCreate = false;
      this.closeEdit();
      await this.load();
    },

    async onImportCompleted() {
      this.showImport = false;
      await this.load();
    },
  },

  async mounted() {
    await this.loadFinancialYears();
    await this.load();
    document.addEventListener("click", this.onDocClick);
  },

  beforeUnmount() {
    clearTimeout(this._qTimer);
    document.removeEventListener("click", this.onDocClick);
  },
};
</script>

<style scoped>
.financial-year-summary {
  min-height: 42px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.avatar {
  width: 40px;
  height: 40px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  font-weight: 800;
  background: #f1f5f9;
  color: #0f172a;
  border: 1px solid #e2e8f0;
}

.min-w-0 {
  min-width: 0;
}

.wrap-anywhere {
  display: inline-block;
  max-width: 100%;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 13px;
  color: #0f172a;
}

.chip-x {
  border: 0;
  background: transparent;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  line-height: 1;
  display: grid;
  place-items: center;
  color: #64748b;
}

.chip-x:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #0f172a;
}

.action-btn {
  width: 34px;
  height: 34px;
  padding: 0;
  border-radius: 10px;
  display: inline-grid;
  place-items: center;
}

.action-pop {
  position: absolute;
  right: 12px;
  top: calc(100% + 6px);
  min-width: 210px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 6px;
  z-index: 1050;
}

.action-item {
  width: 100%;
  text-align: left;
  border: 0;
  background: transparent;
  padding: 10px 12px;
  border-radius: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.action-item:hover {
  background: rgba(13, 110, 253, 0.08);
}

.action-item.text-danger:hover {
  background: rgba(220, 53, 69, 0.08);
}

.action-item.text-success:hover {
  background: rgba(25, 135, 84, 0.08);
}

.action-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 6px 0;
  border-radius: 999px;
}

/* Prevent horizontal scroll */
.members-table-wrap {
  overflow-x: hidden !important;
}

.table {
  table-layout: fixed;
  width: 100%;
}

/* Column sizing */
.member-col {
  width: 26%;
}

.phone-col {
  width: 14%;
}

.email-col {
  width: 18%;
}

.role-col {
  width: 10%;
}

.status-col {
  width: 10%;
}

.opening-col,
.commitment-col {
  width: 10%;
}

.action-col {
  width: 60px;
}

/* Prevent content forcing width */
.email-cell,
.min-w-0 {
  min-width: 0;
}

.email-text {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.table td,
.table th {
  vertical-align: middle;
  white-space: normal;
}

/* Better badge wrapping */
.badge {
  white-space: nowrap;
  max-width: 100%;
}

.mobile-actions .btn {
  flex: 1 1 auto;
}

.beneficiary-col {
  width: 11%;
}

.beneficiary-panel {
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
}

.beneficiary-mobile-panel {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0.75rem;
  background: #f8fafc;
}

.beneficiary-mobile-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.beneficiary-mobile-item:last-child {
  border-bottom: 0;
  padding-bottom: 0;
}

/* Tablet responsiveness */
@media (max-width: 1199px) {
  .member-col {
    width: 24%;
  }

  .email-col {
    width: 16%;
  }

  .phone-col {
    width: 13%;
  }
}
</style>
